import type { JSX } from 'react';

import { Link, Text } from '@change/ds-core-ui';
import { ArrowRightIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { ModuleSeparator } from 'src/app/pages/starterDashboardV2/shared/components';

import { PetitionStats } from './components/PetitionStats';
import { PetitionStrengthScore } from './components/PetitionStrengthScore';
import { ReachedYourGoal } from './components/ReachedYourGoal';
import { usePetitionOverview } from './hooks/usePetitionOverview';

export function PetitionOverview(): JSX.Element {
	const {
		data: { strengthTipsEnabled },
	} = usePetitionOverview();
	const { slug } = useKnownAvailableParams<{ slug: string }>();

	const {
		data: {
			viewer: {
				experiments: {
					starterExperiencePetitionActivityV1: { variation: activityPageVariation },
				},
			},
		},
	} = useStarterDashboardV2PageData();

	return (
		<section
			className="bg-white px-4 py-8 md:p-12 md:pt-4 lg:pt-12"
			aria-labelledby="petition-overview-title"
			role="group"
		>
			<div className="flex items-center justify-between md:mb-6">
				<Text
					className="text-size-heading-small lg:text-size-heading"
					size="small"
					as="h3"
					id="petition-overview-title"
					data-qa="petition-overview-title"
					data-testid="petition-overview-title"
				>
					<Translate value="corgi.petition.starter_dashboard.petition_overview.title" />
				</Text>

				{activityPageVariation === 'enabled' && (
					<Link
						href={`/p/${encodeURIComponent(slug)}/activity`}
						data-testid="petition-activity-link"
						variant="secondary"
					>
						<Text className="text-size-body-bold sm:hidden md:inline">Petition activity</Text>
						<div className="flex flex-row items-center gap-[3px] md:hidden">
							<Text className="text-size-body-bold">All</Text>
							<ArrowRightIcon size="small" />
						</div>
					</Link>
				)}
			</div>
			<div
				className={cn('grid grid-cols-[1fr] grid-rows-[auto_1fr] gap-y-2 border-[unset] pt-4 md:gap-y-0 md:pt-0', {
					'md:grid-cols-[2fr_1fr]': strengthTipsEnabled,
				})}
			>
				<div className="relative">
					<PetitionStats />
				</div>
				<div className="flex w-full flex-col items-center justify-center border-none border-[#E1DDE9] p-0 md:min-w-[240px] md:items-start md:border-l-[1px] md:border-solid md:pl-8 lg:min-w-[300px] lg:pl-20 lg:pr-0">
					{strengthTipsEnabled && <PetitionStrengthScore />}
					<ModuleSeparator orientation="horizontal" className="hidden md:my-6 md:block" />
					<ReachedYourGoal />
				</div>
			</div>
		</section>
	);
}
